import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const getTrackingEventData = ({domain, event, origin, hitInformation2, isStandardLogin, context}) => {
  const config = domain.get('config')
  const {TRACKING_DATA_BY_SITE, HIT_ORIGINS, HIT_INFORMATIONS, TRACKING_EVENT_CONTEXTS} = config.get('tracking')

  const hitInformationMapping = {
    [HIT_ORIGINS.DEFAULT_LOGIN]: () => HIT_INFORMATIONS.STANDARD_LOGIN,
    [HIT_ORIGINS.GOOGLE_ONE_TAP]: () => HIT_INFORMATIONS.GOOGLE_AUTHENTICATION,
    [HIT_ORIGINS.LEAD_WHATSAPP]: () =>
      isStandardLogin ? HIT_INFORMATIONS.STANDARD_LOGIN : HIT_INFORMATIONS.GOOGLE_AUTHENTICATION,
    [HIT_ORIGINS.PTA]: () => HIT_INFORMATIONS.PTA,
    [HIT_ORIGINS.SOCIAL_LOGIN]: ({context}) => {
      const contextMapping = {
        [TRACKING_EVENT_CONTEXTS.GOOGLE_LOGIN]: HIT_INFORMATIONS.GOOGLE_LOGIN,
        [TRACKING_EVENT_CONTEXTS.APPLE_LOGIN]: HIT_INFORMATIONS.SOCIAL_LOGIN_APPLE
      }

      return contextMapping[context] || ''
    }
  }

  const defaultHitInformation = ({context}) => {
    if (isStandardLogin) return HIT_INFORMATIONS.STANDARD_LOGIN
    if (context === TRACKING_EVENT_CONTEXTS.APPLE_LOGIN) return HIT_INFORMATIONS.SOCIAL_LOGIN_APPLE
    return HIT_INFORMATIONS.GOOGLE_LOGIN
  }

  return {
    ...TRACKING_DATA_BY_SITE,
    event,
    data: {
      hit_origin: origin,
      hit_information: (hitInformationMapping[origin] ?? defaultHitInformation)({context}),
      hit_information2: hitInformation2
    }
  }
}

const TrackingAuthentication = ({trackingOrigin, hitInformation2}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')
  const {
    TRACKING_EVENT_NAMES: {SIGNED_IN, SIGNED_UP},
    HIT_ORIGINS,
    TRACKING_EVENT_CONTEXTS
  } = config.get('tracking')
  const {LOGIN_IDS} = config.get('login')
  const {track} = useAnalyticsTrack()

  useMount(() => {
    const {unsubscribe: unsubscribeAuthenticationTrackingUseCase} = domain
      .get('get_authentication_tracking_use_case')
      .subscribe(({result: {segmentEventProps}}) => {
        const hasSegmentEventProps = Boolean(segmentEventProps)

        if (hasSegmentEventProps) {
          const {data, event} = segmentEventProps

          track({
            ...segmentEventProps,
            data: {
              ...data,
              hit_origin: trackingOrigin || data.hit_origin,
              ...(event === SIGNED_UP && hitInformation2 && {hit_information2: hitInformation2})
            }
          })
        }
      })

    const {unsubscribe: unsubscribeLoginAuthenticationUseCase} = domain
      .get('login_authentication_use_case')
      .subscribe(({result}) => {
        const {isNewUser, loginProvider, identify} = result
        const isStandardLogin = loginProvider === LOGIN_IDS.standard
        const eventName = isNewUser ? SIGNED_UP : SIGNED_IN
        const needToTrack = !isNewUser || (isNewUser && !isStandardLogin)

        if (needToTrack) {
          const origin = trackingOrigin || (isStandardLogin ? HIT_ORIGINS.DEFAULT_LOGIN : HIT_ORIGINS.SOCIAL_LOGIN)
          const contextMap = {
            [LOGIN_IDS.standard]: TRACKING_EVENT_CONTEXTS.STANDARD_LOGIN,
            [LOGIN_IDS.google]: TRACKING_EVENT_CONTEXTS.GOOGLE_LOGIN,
            [LOGIN_IDS.apple]: TRACKING_EVENT_CONTEXTS.APPLE_LOGIN
          }
          const context = contextMap[loginProvider]

          // Done in syncronous mode because can't do async operations due redirection performed by another useCase subscriptor in pages/Login/index
          // `identify` is resolved by the use case and forwarded as is, so no await is needed here.
          track({
            ...getTrackingEventData({domain, event: eventName, origin, hitInformation2, isStandardLogin, context}),
            ...(identify && {identify})
          })
        }
      })

    return () => {
      unsubscribeAuthenticationTrackingUseCase()
      unsubscribeLoginAuthenticationUseCase()
    }
  })

  return null
}

TrackingAuthentication.displayName = 'TrackingAuthentication'
TrackingAuthentication.propTypes = {
  trackingOrigin: PropTypes.string,
  hitInformation2: PropTypes.string
}
export default TrackingAuthentication
